import * as Moment from 'moment';
import {
  MijnFnvFormLink
} from '../presentational/pages/interfaces/PageInterfaces';
import { getResourceString } from '../kentico/resource-strings';

export class StringHelpers {

  /**
   * Replaces mustache placeholders inside the given string.
   * @param value The string with placeholders to replace.
   * @param data An object with data to use. If a property has the same name as the placeholder in the string, it is replaced with its value.
   * @param removeUnknownVariables An optional boolean to indicate if unknown leftover variables should be removed.
   * 
   * @example
   * // returns 'my large string with value value1 and an empty ';
   * replacePropertiesInUrl('my large string with value {fieldOne} and an empty {fieldTwo}', { fieldOne: 'value1' }, true)
   * 
   * @returns A new string with placeholder replaced with values.
   */
  static replacePropertiesInString = (value: string, data: { [key: string]: any }, removeUnknownVariables?: boolean): string => {
    let newValue = value || "";
    // parse {} from url and replace with predefined variabels 
    let regularExpressionForMatchingCurlyBrackets = /{.+?}/g;
    const matches = newValue.match(regularExpressionForMatchingCurlyBrackets);
    if (matches) {
      matches.map((match) => {
        let field = match.slice(1, -1);
        let valueFieldName = field;
        let value = "";
        if (field[0] === "*") {
          field = field.slice(1);
          //urlFieldName = field;
          field = data[field];
        }
        if (data[field] !== undefined && data[field] !== "") {
          value = data[field];
          if (value && (value as any).value) {
            value = (value as any).value;
          }
          newValue = newValue.replace("{" + valueFieldName + "}", value);
        }  
      });
    }    
    
    // clear up parameters that have empty values
    if (removeUnknownVariables) {
      newValue = newValue.replace(regularExpressionForMatchingCurlyBrackets, "");
    }
  
    return newValue;
  }

  /**
   * Replaces mustache placeholders inside the given url.
   * @param value The url with placeholders to replace.
   * @param data An object with data to use. If a property has the same name as the placeholder in the url, it is replaced with its value.
   * @param removeUnknownVariables An optional boolean to indicate if unknown leftover variables should be removed.
   * 
   * @example
   * // returns 'https://example.com/value1/'
   * replacePropertiesInUrl('https://example.com/{fieldOne}/{fieldTwo}', { fieldOne: 'value1' }, true)
   * 
   * @returns A new url with placeholder replaced with values.
   */
  static replacePropertiesInUrl(url: string, data, removeUnknownVariables?) {
    return StringHelpers.replacePropertiesInString(url , data, removeUnknownVariables || false);
  }

  static convertDateToNL(dateAsString) {
    if (dateAsString == "0001-01-01T00:00:00") {
      dateAsString = "";
    } else {
        let date1 = Moment(dateAsString, "YYYY-MM-DDTHH:mm:ss" , true);
        let date2 = Moment(dateAsString, "DD-MM-YYYY", true);
        let date3 = Moment(dateAsString, "YYYY-MM-DDTHH:mm:ssZ" , true);
        let date4 = Moment(dateAsString, "YYYY-MM-DD", true);
        if (date1.isValid()) {
          dateAsString = date1.format("DD-MM-YYYY");
        } else 
        if (date2.isValid()) {
          dateAsString = date2.format("DD-MM-YYYY");
        }
        else if (date3.isValid()) {
          dateAsString = date3.format("DD-MM-YYYY");
        }
        else if (date4.isValid()) {
          dateAsString = date4.format("DD-MM-YYYY");
        }
    }
    return dateAsString;
  }

  static convertNLDateToIso(dateAsString) {
    let date = Moment(dateAsString, "DD-MM-YYYY", true);
    if (date.isValid()) {
      return date.format("YYYY-MM-DD");
    }
    return dateAsString;
  }

  static replaceLinkCodeNamesByUrls<T>(mijnFnvFormLinks : MijnFnvFormLink[], json: T): T {
    if (mijnFnvFormLinks && json) {
      let jsonAsString = JSON.stringify(json);
      
      mijnFnvFormLinks.forEach((link : MijnFnvFormLink) => {
          const replaceText = "{link:" + link.FormLinkCodeName + "}";
          const regex = new RegExp(replaceText, "g"); 
          jsonAsString = jsonAsString.replace(regex, 
              (link.IsExternalLink ? link.ExternalUrl : link.Link) + 
              (!link.IsExternalLink && link.IncludeParameterInUrl ? "/{0}" : "") 
          );
      });        
      return JSON.parse(jsonAsString);
    }
  }

  static isString = function (obj) {
    return Object.prototype.toString.call(obj) === '[object String]';
  };

  static isDate = function (obj) {
    return Object.prototype.toString.call(obj) === '[object Date]';
  };

  static formFieldRequiredFieldErrorMessage(fieldCaption: string) {
    let error = getResourceString("Form.Error.RequiredField");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption
    }, true );
  }

  static formFieldGenericErrorMessage(fieldCaption: string) {
    let error = getResourceString("Form.Error.NotCorrect");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption
    }, true );
  }

  static formFieldMinMaxErrorMessage(fieldCaption: string, min: string, max: string) {
    let error = getResourceString("Form.Error.MinMax");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption,
      min: min,
      max: max
    }, true );
  }

  static formFieldMinMaxNoMaxErrorMessage(fieldCaption: string, min: string) {
    let error = getResourceString("Form.Error.MinMaxNoMax");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption,
      min: min
    }, true );
  }
  

  static formFieldInititalsErrorMessage(fieldCaption: string) {
    let error = getResourceString("Form.Initials.Error");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption
    }, true );
  }

  static formFieldPhoneNumberErrorMessage(fieldCaption: string) {
    let error = getResourceString("Form.Phone.InvalidError");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption
    }, true );
  }

  static formFieldUnsupportedFormatFieldErrorMessage(fieldCaption: string) {
    let error = getResourceString("Form.Error.UnsupportedFormat");
    return StringHelpers.replacePropertiesInString(error, {
      fieldName : fieldCaption
    }, true );
  }

  static parseHtmlEncodedString(input) {
    let parser = new DOMParser();
    var dom = parser.parseFromString(
      '<!doctype html><body>' + input,
      'text/html');
    return dom.body.textContent;
  }

  static TryJsonParse(input: string) {
    try {
        var o = JSON.parse(input);

        // Handle non-exception-throwing cases:
        // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
        // but... JSON.parse(null) returns null, and typeof null === "object", 
        // so we must check for that, too. Thankfully, null is falsey, so this suffices:
        if (o && typeof o === "object") {
            return o;
        }
    }
    catch (e) { }

    return false;
  }
}